import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { useSeo } from '@/hooks/useSeo';
import { characters } from '@/mocks/characters';
import { categories } from '@/mocks/categories';

export default function CharactersPage() {
  useSeo({
    title: '角色百科 - 妖精动漫角色大全与动漫人物介绍 | 妖精动漫网',
    description: '妖精动漫网角色百科，收录森林精灵、花之妖精、星之精灵、暗夜精灵等妖精动漫人物介绍。支持按分类与关键词快速检索，附人气榜与配音演员信息。',
  });

  const [params, setParams] = useSearchParams();
  const activeCat = params.get('c') ?? 'all';
  const keyword = params.get('q') ?? '';
  const [kw, setKw] = useState(keyword);

  const filtered = useMemo(() => {
    return characters.filter((c) => {
      const matchCat = activeCat === 'all' || c.categoryId === activeCat;
      const q = keyword.trim().toLowerCase();
      const matchKw =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.work.toLowerCase().includes(q) ||
        c.seiyuuName.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q));
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  const setCategory = (id: string) => {
    const next = new URLSearchParams(params);
    if (id === 'all') next.delete('c');
    else next.set('c', id);
    if (keyword) next.set('q', keyword);
    setParams(next, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (kw.trim()) next.set('q', kw.trim());
    else next.delete('q');
    setParams(next, { replace: true });
  };

  return (
    <PageLayout>
      {/* 页头横幅 */}
      <section className="relative overflow-hidden pt-16 md:pt-18">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20fantasy%20banner%20with%20portraits%20of%20various%20anime%20elves%20and%20fairies%20in%20a%20row%2C%20emerald%20and%20gold%20glowing%20background%2C%20magical%20atmosphere%2C%20soft%20painterly%20style%2C%20clean%20minimal%20composition&width=1600&height=420&seq=char-banner&orientation=landscape"
          alt="各类妖精精灵角色肖像的奇幻横幅背景"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 to-primary-800/75" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="mb-3 flex items-center gap-1.5 text-xs text-background-100/80">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">角色百科</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">角色百科</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-6 text-background-100/90">
            集中收录妖精、精灵类动漫作品的角色百科与动漫人物介绍。按作品或角色名直接查找，附人气榜与配音演员信息。
          </p>
        </div>
      </section>

      {/* 筛选与列表 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
        {/* 搜索栏 */}
        <form onSubmit={submitSearch} className="mb-5 flex flex-col sm:flex-row gap-3">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200/70 bg-background-50 px-4 py-2.5 focus-within:border-primary-300">
            <i className="ri-search-line text-foreground-400" />
            <input
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="输入角色名、作品名或声优名…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400"
              aria-label="搜索角色"
            />
          </div>
          <button type="submit" className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors">
            搜索
          </button>
        </form>

        {/* 分类筛选 */}
        <div className="mb-7 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCategory('all')}
            className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
              activeCat === 'all' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setCategory(cat.id)}
              className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                activeCat === cat.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
              <span className="ml-1 text-xs opacity-70">{cat.count}</span>
            </button>
          ))}
        </div>

        <p className="mb-5 text-sm text-foreground-600">
          共找到 <strong className="text-primary-700">{filtered.length}</strong> 位角色
          {keyword && <> · 关键词「<strong className="text-foreground-950">{keyword}</strong>」</>}
        </p>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 md:gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((char) => (
              <Link
                key={char.id}
                to={`/character/${char.id}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-transform hover:-translate-y-1"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={char.image}
                    alt={`${char.name} - ${char.title}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-xs font-bold text-primary-900">
                    {char.rank}
                  </span>
                  <span className="absolute right-2 top-2 rounded-full bg-background-950/55 px-2 py-0.5 text-xs text-background-50 backdrop-blur">
                    {char.category}
                  </span>
                </div>
                <div className="p-3">
                  <h2 className="truncate font-medium text-sm text-foreground-950">{char.name}</h2>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">{char.title} · {char.work}</p>
                  <p className="mt-2 flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-mic-line text-accent-600" />
                    {char.seiyuuName}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-background-200/70 bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" />
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的角色，换个关键词或分类试试吧。</p>
          </div>
        )}
      </section>
    </PageLayout>
  );
}